// Common next-entry-number generation for all entry forms (sales, purchase, etc).
//
// Every entry form used to download its complete transaction list from the API
// just to find the highest existing entry number. That call returns every
// invoice with full details and can take ~20 seconds on large databases.
// Instead, the list page that opens the modal already holds exactly that data,
// so the modal can compute the next number from it instantly and only falls
// back to the API when no cached list is available.

// Highest entry number saved during this browser session, per branch+prefix.
// Guards against reusing a number when the user saves an entry and immediately
// opens the form again while the parent list is still refreshing.
const issuedNumbers = {};

const registryKey = (branchGuid, prefix) =>
  `${branchGuid || ""}:${(prefix || "").toUpperCase()}`;

const numericPart = (entrynumber) => {
  const match = entrynumber?.toString().match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

// Call after a successful save so the number is never handed out again
// before the cached list catches up.
export const registerEntryNumber = (branchGuid, entrynumber) => {
  const num = numericPart(entrynumber);
  if (num === null) return;
  const prefixMatch = entrynumber.toString().match(/^[A-Za-z]+/);
  const key = registryKey(branchGuid, prefixMatch ? prefixMatch[0] : "");
  if (!issuedNumbers[key] || issuedNumbers[key] < num) {
    issuedNumbers[key] = num;
  }
};

// records: array of objects carrying an `entrynumber` field.
// prefix: the form's entry prefix, e.g. "PI", "PO", "GR", "PR", "SI", "SQ", "SR", "DN".
export const getNextEntryNumber = (records, prefix, branchGuid) => {
  const list = Array.isArray(records) ? records : [];
  const upperPrefix = (prefix || "").toUpperCase();

  // Prefer entries carrying this form's prefix so a stray entry number from
  // another series can't inflate the sequence; fall back to all entries so
  // legacy data without a prefix still continues its sequence.
  const prefixed = list.filter((r) =>
    r?.entrynumber?.toString().toUpperCase().startsWith(upperPrefix)
  );
  const source = prefixed.length > 0 ? prefixed : list;

  const numbers = source
    .map((r) => numericPart(r?.entrynumber))
    .filter((n) => n !== null);
  const maxInRecords = numbers.length > 0 ? Math.max(...numbers) : 0;
  const sessionMax = issuedNumbers[registryKey(branchGuid, upperPrefix)] || 0;

  return `${prefix}${Math.max(maxInRecords, sessionMax) + 1}`;
};

// cachedRecords: the already-loaded list from the page that opened the form.
// fetcher: () => API call returning the list; used only when no cache exists
// (e.g. the form is opened before the list finished loading).
export const fetchNextEntryNumber = async ({
  prefix,
  branchGuid,
  cachedRecords,
  fetcher,
}) => {
  if (Array.isArray(cachedRecords) && cachedRecords.length > 0) {
    return getNextEntryNumber(cachedRecords, prefix, branchGuid);
  }
  const response = await fetcher();
  const records = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];
  return getNextEntryNumber(records, prefix, branchGuid);
};
